import * as bootstrap from 'bootstrap';
import GLightbox from 'glightbox';
import Swiper from 'swiper/bundle';
import Helper from './helper';

import 'swiper/css/bundle';

import.meta.glob([
    '../img/**'
]);

class App {
    constructor() {
        this.init();
    }

    init() {
        this.navbarDropdowns();
        this.navbarCollapse();
        this.navbarObserver();
        this.honeyForms();
        this.asyncForms();
        this.photoSlider();
        this.GLightbox();
        this.cookies();
        this.responsiveEmbeds();
    }

    navbarDropdowns() {
        const navbarList = document.getElementById('main-navbar-list');

        if (navbarList === null) {
            return false;
        }

        navbarList.querySelectorAll('.dropdown').forEach(dropdown => {
            const dropdownToggle = dropdown.querySelector('.dropdown-toggle');

            dropdownToggle.addEventListener('show.bs.dropdown', event => {
                document.body.classList.add('submenu-open');
            });

            dropdownToggle.addEventListener('hide.bs.dropdown', event => {
                setTimeout(() => {
                    // Delay removal to allow another dropdown to be opened immediately
                    const anyOpenDropdown = navbarList.querySelector('.dropdown-menu.show');

                    if (anyOpenDropdown === null) {
                        document.body.classList.remove('submenu-open');
                    }
                }, 100); // Allow enough time for the new dropdown to potentially open
            });
        });

        const submenuDropdownToggles = navbarList.querySelectorAll('.main-navbar__submenu-dropdown-toggle');

        submenuDropdownToggles.forEach(submenuDropdownToggle => {
            const dropdownMenu = submenuDropdownToggle.closest('.main-navbar__submenu-dropdown').querySelector('.main-navbar__submenu-dropdown-menu');

            submenuDropdownToggle.addEventListener('click', e => {
                e.preventDefault();

                submenuDropdownToggle.classList.toggle('show');

                if (dropdownMenu) {
                    dropdownMenu.classList.toggle('show');
                }
            });
        });
    }

    navbarCollapse() {
        const navbarCollapse = document.getElementById('main-navbar-content');

        if (navbarCollapse === null) {
            return false;
        }

        navbarCollapse.addEventListener('show.bs.collapse', event => {
            document.body.classList.add('navbar-collapse-open');
        });

        navbarCollapse.addEventListener('hide.bs.collapse', event => {
            document.body.classList.remove('navbar-collapse-open');
        });
    }

    navbarObserver() {
        const desktopWidth = 1200;

        const observeWindowSize = () => {
            // Check minimum window width
            if (window.innerWidth < desktopWidth) {
                // Check if the submenu open state is active
                if (document.body.classList.contains('submenu-open')) {
                    // Remove submenu open state
                    document.body.classList.remove('submenu-open');
                }
            }
            else {
                // Check if the collapse open state is active
                if (document.body.classList.contains('navbar-collapse-open')) {
                    // Remove navbar collapse open state
                    document.body.classList.remove('navbar-collapse-open');
                }
            }
        }

        const observeScroll = () => {
            const scrollPosition = window.scrollY || document.documentElement.scrollTop;

            // Check scroll position
            if (scrollPosition > 50) {
                // Add sticky menu state
                document.body.classList.add('navbar-sticky');
            }
            else {
                // Remove sticky menu state
                document.body.classList.remove('navbar-sticky');
            }
        }

        window.addEventListener('resize', Helper.debounce(() => observeWindowSize()));

        // Create sticky menu state upon scroll
        window.addEventListener('scroll', () => observeScroll());

        // Initial check
        observeScroll();
    }

    /**
     * Honey pot protection for forms
     */
    honeyForms() {
        // Initialize honey pot for a single form
        const initHoneyForm = (form) => {
            // Skip if already initialized
            if (form.dataset.honeyInitialized === 'true') {
                return;
            }

            // Mark as initialized
            form.dataset.honeyInitialized = 'true';

            // Randomize delay between 4-6 seconds to be less predictable
            const delay = 4000 + Math.random() * 2000;

            setTimeout(() => {
                let buttonSubmit = form.querySelector('button[type="submit"]');

                if (buttonSubmit) {
                    // Restore original button text
                    const originalText = buttonSubmit.getAttribute('data-text');

                    if (originalText) {
                        buttonSubmit.innerHTML = originalText;
                        buttonSubmit.disabled = false;
                    }
                }

                let coffeeCup = form.querySelector('.coffee-cup');

                if (coffeeCup) {
                    // Fill the coffee cup
                    coffeeCup.value = 'filled';

                    // For Livewire-bound fields, trigger input event to sync with wire:model
                    if (coffeeCup.hasAttribute('wire:model') || coffeeCup.hasAttribute('wire:model.defer')) {
                        coffeeCup.dispatchEvent(new Event('input', { bubbles: true }));
                    }
                }
            }, delay);
        };

        // Initialize all existing honey forms
        document.querySelectorAll('.form--honey').forEach(form => {
            initHoneyForm(form);
        });

        // Watch for dynamically added forms
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    // Check if the added node is a form with honey class
                    if (node.nodeType === 1) {
                        if (node.classList && node.classList.contains('form--honey')) {
                            initHoneyForm(node);
                        }

                        // Also check for honey forms within the added node
                        if (node.querySelectorAll) {
                            node.querySelectorAll('.form--honey').forEach(form => {
                                initHoneyForm(form);
                            });
                        }
                    }
                });
            });
        });

        // Start observing the document for changes
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    }

    /**
     * Process asynchronous forms
     */
    asyncForms() {
        let gRecaptchaWidgets = [];

        document.querySelectorAll('.g-recaptcha-widget').forEach(recaptcha => {
            let widgetId = grecaptcha.render(recaptcha.id, {
                'sitekey' : config.recaptcha_site_key
            });

            gRecaptchaWidgets.push(widgetId);
        });

        /**
         * Refresh reCAPTCHA
         */
        let refreshRecaptcha = () => {
            // Check if reCAPTCHA is loaded
            if (typeof grecaptcha === 'undefined') {
                return;
            }

            // Refresh all recaptcha widget instances
            gRecaptchaWidgets.forEach(widgetId => {
                grecaptcha.reset(widgetId);
            });
        }

        setInterval(function() {
            refreshRecaptcha();
        }, 2 * 60 * 1000);

        document.querySelectorAll('.form--async').forEach(form => {
            form.addEventListener('submit', (e) => {
                e.preventDefault();

                // Serialize all form data
                let formData = new FormData(form);

                // Fetch container for the form messages
                let formMessages = form.querySelector('.form-messages');

                // Clear the form errors
                form.querySelectorAll('[data-error]').forEach(errorMessage => {
                    // Clear error message
                    errorMessage.innerHTML = '';

                    // Remove multiple status class
                    errorMessage.classList.remove('multiple');
                });

                // Clear the form messages
                formMessages.replaceChildren();

                Helper.post(form.getAttribute('action'), formData).then(response => {
                    if (response.redirect_url) {
                        // Redirect to the given URL
                        window.location.href = response.redirect_url;
                    }
                    else {
                        // Reset form
                        form.reset();

                        // Refresh reCAPTCHA
                        refreshRecaptcha();

                        formMessages.innerHTML = '<div class="alert alert--' + response.message.type + '"><span>' + response.message.text + '</span></div>';
                    }
                })
                .catch(error => {
                    let statusCode = error.statusCode;

                    switch (statusCode) {
                        case 422:
                            // Set error messages
                            Object.entries(error.response.errors).forEach(([key, errors]) => {
                                // Find the target element with data-error attribute
                                const targetElement = form.querySelector('[data-error="' + key + '"]');

                                if (targetElement === null) {
                                    return;
                                }

                                if (errors.length > 1) {
                                    // Mark container as multiple errors
                                    targetElement.classList.add('multiple');
                                }

                                errors.forEach(function (error) {
                                    // Create a new <span> element
                                    const errorSpan = document.createElement('span');

                                    // Set the text content of the <span>
                                    errorSpan.textContent = error;

                                    // Append the <span> as a child of the target element
                                    targetElement.appendChild(errorSpan);
                                });
                            });
                            break;

                        default:
                            // Show notification
                            alert('Er is iets misgegaan.');
                    }

                    // Refresh reCAPTCHA
                    refreshRecaptcha();

                    // Reject the promise
                    return Promise.reject(error.statusText);
                });
            });
        });
    }

    photoSlider() {
        document.querySelectorAll('.photos-slider').forEach(element => {
            let photoSlide = element.querySelector('.photos-slider__listing');
            let thumbSlider = element.querySelector('.photos-slider__thumbs');

            let thumbsSwiper = new Swiper('#' + thumbSlider.getAttribute('id'), {
                direction: 'horizontal',
                loop: false,
                freeMode: true,
                watchSlidesProgress: true,
                breakpoints: {
                    0: {
                        spaceBetween: 10,
                        slidesPerView: 2
                    },
                    768: {
                        spaceBetween: 20,
                        slidesPerView: 3
                    }
                }
            });

            new Swiper('#' + photoSlide.getAttribute('id'), {
                spaceBetween: 20,
                loop: false,
                thumbs: {
                    swiper: thumbsSwiper
                },
                navigation: {
                    prevEl: element.parentElement.querySelector('.photos-slider__nav-arrow--previous'),
                    nextEl: element.parentElement.querySelector('.photos-slider__nav-arrow--next')
                }
            });
        });
    }

    GLightbox() {
        const lightbox = GLightbox();
    }

    /**
     * Set cookie preferences
     */
    cookies() {
        let form = document.getElementById('form-cookies');

        if (form === null) {
            return false;
        }

        // Show the cookie form
        form.classList.remove('fade');

        // Add cookie selection state
        document.body.classList.add('cookie-selection');

        let options = form.querySelector('.cookies-banner__options');

        let textChecked   = 'Alle cookies accepteren';
        let textUnchecked = 'Geselecteerde cookies accepteren'

        let buttonSubmit = form.querySelector('button[type="submit"]');

        let isAllCookiesChecked = function () {
            let checked = true;

            options.querySelectorAll('.form-check-input').forEach(switchElement => {
                if (switchElement.checked === false) {
                    checked = false;
                }
            });

            return checked;
        }

        options.querySelectorAll('.form-check-input').forEach(input => {
            // Bind the change event listener to the form check input
            input.addEventListener('change', function () {
                buttonSubmit.innerText = isAllCookiesChecked() ? textChecked : textUnchecked;
            });

            // Manually create on on-change event
            let event = new Event('change');

            // Manually dispatch event
            input.dispatchEvent(event);
        });

        const collapseCookies = document.getElementById('collapse-cookies');

        collapseCookies.addEventListener('show.bs.collapse', () => {
            collapseCookies.querySelectorAll('.form-check-input:not(#cookies-functional)').forEach(input => {
                input.checked = false;
            });

            buttonSubmit.innerText = textUnchecked;
        })

        form.addEventListener('submit', function(e) {
            e.preventDefault();

            let formData = new FormData(this);

            if (e.submitter) {
                // Manually add button name and value
                formData.append(e.submitter.name, e.submitter.value);
            }

            Helper.post(this.action, formData).then(() => {
                // Remove cookie selection state
                document.body.classList.remove('cookie-selection');

                // Fade out the form
                form.classList.add('fade');

                setTimeout(function () {
                    // Reload page
                    location.reload();
                }, 1000);
            })
            .catch(error => {
                // Reject the promise
                return Promise.reject(error.statusText);
            });
        });
    }

    responsiveEmbeds() {
        let wrap = function (toWrap, wrapper) {
            wrapper = wrapper || document.createElement('div');
            toWrap.parentNode.insertBefore(wrapper, toWrap);
            return wrapper.appendChild(toWrap);
        }

        // Responsive embeds
        document.querySelectorAll('iframe:not([class])').forEach(iframe => {
            let wrapper = document.createElement('div');

            wrapper.setAttribute('class', 'ratio ratio-16x9');

            wrap(iframe, wrapper);
        });
    }
}

window.addEventListener('load', () => new App());
